import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { toast } from 'react-hot-toast';
import OnboardingStepVerifyEmail from './OnboardingStepVerifyEmail';
import OnboardingStepWelcome from './OnboardingStepWelcome';
import OnboardingStepUsername from './OnboardingStepUsername';
import OnboardingStepProfile from './OnboardingStepProfile';
import OnboardingStepStripe from './OnboardingStepStripe';
import OnboardingStepFinish from './OnboardingStepFinish';

const initialProfileState = {
  name: '',
  username: '',
  professionalTitle: '',
  bio: '',
  profilePicture: '',
  services: [],
  portfolio: [],
  contactInfo: {
    email: '',
    phone: '',
    location: ''
  }
};

const OnboardingFlow = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(5);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(initialProfileState);
  const [usernameExists, setUsernameExists] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [onboardingStartedAt] = useState(new Date().toISOString());
  const [hasSubscribed, setHasSubscribed] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const autoSaveTimeoutRef = useRef(null);

  // Debounced auto-save function
  const autoSaveProfile = useCallback(async (profileData) => {
    if (!auth.currentUser) return;

    try {
      const profileRef = doc(db, 'profiles', auth.currentUser.uid);
      const profileSnap = await getDoc(profileRef);
      const existingData = profileSnap.exists() ? profileSnap.data() : {};

      const dataToSave = {
        ...existingData,
        ...profileData,
        username: profileData.username ? profileData.username.toLowerCase() : existingData.username,
        updatedAt: new Date().toISOString(),
        onboardingProgress: existingData.onboardingProgress || {
          currentStep,
          stepHistory: [],
          startedAt: onboardingStartedAt,
          subscriptionStatus: 'not_started'
        }
      };

      if (profileData.name || profileData.username) {
        if (!profileSnap.exists()) {
          dataToSave.createdAt = new Date().toISOString();
        }
        await setDoc(profileRef, dataToSave, { merge: true });
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  }, [currentStep, onboardingStartedAt]);

  const triggerAutoSave = useCallback((profileData) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveProfile(profileData);
    }, 1000);
  }, [autoSaveProfile]);

  // Load existing profile data on mount
  useEffect(() => {
    const loadExistingProfile = async () => {
      if (!auth.currentUser) return;

      try {
        const profileRef = doc(db, 'profiles', auth.currentUser.uid);
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          const existingProfile = profileSnap.data();
          if (existingProfile.name || existingProfile.username) {
            setProfile(prevProfile => ({
              ...prevProfile,
              name: existingProfile.name || prevProfile.name,
              username: existingProfile.username || prevProfile.username,
              professionalTitle: existingProfile.professionalTitle || prevProfile.professionalTitle,
              bio: existingProfile.bio || prevProfile.bio,
              profilePicture: existingProfile.profilePicture || prevProfile.profilePicture,
              services: existingProfile.services || prevProfile.services,
              portfolio: existingProfile.portfolio || prevProfile.portfolio,
              contactInfo: {
                ...prevProfile.contactInfo,
                ...existingProfile.contactInfo
              }
            }));
          }
        }
      } catch (error) {
        console.error('Error loading existing profile:', error);
      }
    };

    loadExistingProfile();
  }, []);

  const updateOnboardingProgress = async (step, skipped = false) => {
    if (!auth.currentUser) return;

    try {
      const profileRef = doc(db, 'profiles', auth.currentUser.uid);
      const profileSnap = await getDoc(profileRef);
      const currentData = profileSnap.exists() ? profileSnap.data() : {};
      const currentProgress = currentData.onboardingProgress || {
        stepHistory: [],
        startedAt: onboardingStartedAt,
        subscriptionStatus: 'not_started'
      };

      const stepHistory = [
        ...currentProgress.stepHistory,
        {
          step,
          completedAt: new Date().toISOString(),
          skipped
        }
      ];

      let subscriptionStatus = currentProgress.subscriptionStatus;
      if (step === 5) {
        subscriptionStatus = skipped ? 'skipped' : 'completed';
        setHasSubscribed(!skipped);
      }

      await setDoc(profileRef, {
        ...currentData,
        onboardingProgress: {
          ...currentProgress,
          currentStep: step,
          stepHistory,
          subscriptionStatus,
          lastUpdated: new Date().toISOString()
        }
      }, { merge: true });
    } catch (error) {
      console.error('Error updating onboarding progress:', error);
    }
  };

  useEffect(() => {
    const checkUserAndProfile = async () => {
      if (!auth.currentUser) {
        navigate('/');
        return;
      }

      if (sessionStorage.getItem('isNewUser') !== 'true') {
        navigate('/app/emails');
        return;
      }

      try {
        // Check email verification status
        // Google sign-ups are auto-verified; email/password need manual verification
        await auth.currentUser.reload();
        setEmailVerified(auth.currentUser.emailVerified);

        const profileRef = doc(db, 'profiles', auth.currentUser.uid);
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          const profileData = profileSnap.data();
          if (profileData.username && profileData.name) {
            sessionStorage.removeItem('isNewUser');
            navigate('/app/emails');
            return;
          }
        }

        if (auth.currentUser.email) {
          setProfile(prev => ({
            ...prev,
            contactInfo: { ...prev.contactInfo, email: auth.currentUser.email }
          }));
        }

        if (auth.currentUser.displayName) {
          setProfile(prev => ({ ...prev, name: auth.currentUser.displayName }));
        }
      } catch (error) {
        console.error('Error checking profile:', error);
      }
    };

    checkUserAndProfile();
  }, [navigate]);

  const checkUsername = async (username) => {
    if (!username.trim()) {
      setUsernameExists(false);
      return;
    }

    setCheckingUsername(true);
    try {
      const profilesRef = collection(db, 'profiles');
      const q = query(profilesRef, where('username', '==', username.toLowerCase()));
      const querySnapshot = await getDocs(q);
      const exists = !querySnapshot.empty && querySnapshot.docs[0].id !== auth.currentUser?.uid;
      setUsernameExists(exists);
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameExists(false);
    } finally {
      setCheckingUsername(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (profile.username) {
        checkUsername(profile.username);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [profile.username]);

  const handleProfileChange = (field, value) => {
    let updatedProfile;
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updatedProfile = {
        ...profile,
        [parent]: { ...profile[parent], [child]: value }
      };
    } else {
      updatedProfile = { ...profile, [field]: value };
    }
    setProfile(updatedProfile);
    triggerAutoSave(updatedProfile);
  };

  const handleNext = async () => {
    if (currentStep === 2 && profile.username) {
      if (checkingUsername || usernameExists) return;
    }

    await updateOnboardingProgress(currentStep, false);

    if (currentStep === totalSteps) {
      await saveProfile();
    } else {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const handleSkip = async () => {
    if (currentStep === 4) {
      toast.error('Please select a plan to continue');
      return;
    }

    await updateOnboardingProgress(currentStep, true);

    if (currentStep < totalSteps) {
      setCurrentStep(prevStep => prevStep + 1);
    } else {
      finishOnboarding();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prevStep => prevStep - 1);
    }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      if (!auth.currentUser) {
        throw new Error('You must be logged in to complete onboarding');
      }

      const profileRef = doc(db, 'profiles', auth.currentUser.uid);
      const profileSnap = await getDoc(profileRef);

      const profileData = {
        ...profile,
        username: profile.username.toLowerCase(),
        updatedAt: new Date().toISOString(),
        onboardingProgress: {
          currentStep: totalSteps,
          stepHistory: [
            ...(profileSnap.exists() ? profileSnap.data().onboardingProgress?.stepHistory || [] : []),
            {
              step: totalSteps,
              completedAt: new Date().toISOString(),
              skipped: false
            }
          ],
          startedAt: onboardingStartedAt,
          completedAt: new Date().toISOString(),
          subscriptionStatus: hasSubscribed ? 'completed' : 'skipped',
          hasCompletedOnboarding: true
        }
      };

      if (!profileSnap.exists()) {
        profileData.createdAt = new Date().toISOString();
      }

      await setDoc(profileRef, profileData, { merge: true });
      finishOnboarding();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Error saving profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const finishOnboarding = () => {
    sessionStorage.removeItem('isNewUser');
    setTimeout(() => {
      onClose();
      navigate('/app/emails');
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <OnboardingStepWelcome onNext={handleNext} onSkip={handleSkip} />;
      case 2:
        return (
          <OnboardingStepUsername
            profile={profile}
            onChange={handleProfileChange}
            onNext={handleNext}
            onSkip={handleSkip}
            onBack={handleBack}
            usernameExists={usernameExists}
            checkingUsername={checkingUsername}
          />
        );
      case 3:
        return (
          <OnboardingStepProfile
            profile={profile}
            onChange={handleProfileChange}
            onNext={handleNext}
            onSkip={handleSkip}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <OnboardingStepStripe
            onNext={handleNext}
            onSkip={handleSkip}
            onBack={handleBack}
          />
        );
      case 5:
        return <OnboardingStepFinish onFinish={saveProfile} loading={loading} />;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  const progress = emailVerified ? (currentStep / totalSteps) * 100 : 0;
  const contentKey = emailVerified ? `step-${currentStep}` : 'verify-email';

  return (
    <div className="fixed inset-0 z-50 bg-[#faf9f7]">
      {/* Minimal top bar */}
      <div className="absolute top-0 left-0 right-0 z-10">
        <div className="flex items-center justify-between px-6 sm:px-10 py-6">
          <span className="text-[11px] tracking-[0.3em] text-gray-400 font-light uppercase">
            K L O R A H
          </span>
          <div className="flex items-center gap-4">
            {emailVerified && (
              <button
                onClick={onClose}
                className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                Skip for now
              </button>
            )}
            <button
              onClick={() => {
                sessionStorage.removeItem('isNewUser');
                auth.signOut();
                navigate('/');
              }}
              className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
        {/* Progress line — full width, ultra thin */}
        <div className="h-[1px] bg-gray-200/60">
          <motion.div
            className="h-full bg-[#f97316]"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
          />
        </div>
      </div>

      {/* Content area — centered */}
      <div className="h-full flex items-center justify-center px-6">
        <div className="w-full max-w-[480px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={contentKey}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            >
              {emailVerified ? renderStep() : (
                <OnboardingStepVerifyEmail onVerified={() => setEmailVerified(true)} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
